import React from 'react';
import { Helmet } from 'react-helmet';

const SchemaOrg = ({ type = 'website', data }) => {
  // Get base URL dynamically - use env var or window location
  const getBaseUrl = () => {
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    // Server-side rendering fallback - use env var if available
    return process.env.REACT_APP_BASE_URL || 'https://schluesseldienst.services';
  };
  
  const baseUrl = getBaseUrl();
  
  const renderWebSiteSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${baseUrl}/#website`,
    url: `${baseUrl}/`,
    name: 'Schlüsseldienst.services',
    publisher: { '@id': `${baseUrl}/#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${baseUrl}/suche?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  });

  const renderLocksmithSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'Locksmith',
    '@id': `${baseUrl}/#organization`,
    name: 'Schössow Dienstleistungen UG (haftungsbeschränkt)',
    legalName: 'Schössow Dienstleistungen UG (haftungsbeschränkt)',
    alternateName: 'Schlüsseldienst Dortmund',
    url: `${baseUrl}/`,
    telephone: '+49-231-39085116',
    email: 'info@schluesseldienst.services',
    image: `${baseUrl}/images/og-image.svg`,
    priceRange: '€€',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Neben dem Brand 10',
      postalCode: '44135',
      addressLocality: 'Dortmund',
      addressRegion: 'NRW',
      addressCountry: 'DE'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '51.5136',
      longitude: '7.4653'
    },
    areaServed: [
      {
        '@type': 'City',
        name: 'Dortmund'
      },
      {
        '@type': 'AdministrativeArea',
        name: 'Dortmund'
      }
    ],
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '00:00',
        closes: '23:59'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday', 'Sunday'],
        opens: '00:00',
        closes: '23:59'
      }
    ],
    paymentAccepted: 'Cash, EC Card, Credit Card',
    vatID: 'DE310018782',
    identifier: 'HRB 29517 (Amtsgericht Dortmund)',
    founder: 'Andreas Schössow',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Schlüsseldienst Leistungen',
      itemListElement: [
        {
          '@type': 'Offer',
          priceCurrency: 'EUR',
          price: '119',
          category: 'door-unlocking',
          name: 'Türöffnung – Mo–Fr 08–20',
          description: 'Schadenfreie Türöffnung bei zugefallener Tür',
          availability: 'https://schema.org/InStock',
          areaServed: 'Dortmund'
        },
        {
          '@type': 'Offer',
          priceCurrency: 'EUR',
          price: '199',
          category: 'door-unlocking',
          name: 'Türöffnung – Sa–So',
          description: 'Schadenfreie Türöffnung bei zugefallener Tür am Wochenende',
          availability: 'https://schema.org/InStock',
          areaServed: 'Dortmund'
        }
      ]
    }
  });

  const renderFAQSchema = (faqItems) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  });

  const renderBreadcrumbSchema = (breadcrumbItems) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${baseUrl}${item.url}`
    }))
  });

  const schemas = [];
  
  if (type === 'website') {
    schemas.push(renderWebSiteSchema());
  }
  
  schemas.push(renderLocksmithSchema());
  
  if (data?.faqItems) {
    schemas.push(renderFAQSchema(data.faqItems));
  }

  if (data?.breadcrumbItems) {
    schemas.push(renderBreadcrumbSchema(data.breadcrumbItems));
  }

  return (
    <Helmet>
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SchemaOrg;
